import { type IGetActionsData, formatDateRange, formatDateWithOptionalTime } from "@api";
import type { IAction } from "@bbj-types/action";
import type { ISettingMaster } from "@bbj-types/setting";
import { tz } from "@date-fns/tz";
import { startOfDay } from "date-fns";

type TGetActionDate = Pick<
  IAction,
  "status" | "dismissDate" | "deadlineDate" | "executionDate" | "approach" | "executionPeriod"
>;
export const getActionDate = (action: TGetActionDate): Date | undefined => {
  if (isActionApproach(action)) {
    return action.executionPeriod?.startDate ?? undefined;
  }
  switch (action.status) {
    case "dismissed":
      return action.dismissDate ?? undefined;
    case "todo":
      return action.deadlineDate ?? undefined;
    case "done":
    case "missed":
      return action.executionDate ?? undefined;
  }
};

export const isActionApproach = (action: Pick<IAction, "approach">) => {
  return action.approach !== undefined;
};

export const isActionLate = (
  action: Pick<IAction, "status" | "date" | "deadlineDate" | "executionPeriod">,
  timeZone: string
) => {
  const date = action.deadlineDate ?? action.executionPeriod?.endDate ?? action.date;

  if (!date || Number.isNaN(date.getTime())) {
    return false;
  }
  return action.status === "todo" && date.getTime() < startOfDay(new Date(), { in: tz(timeZone) }).getTime();
};

export const getActionTodoDateForFilters = (timeZone: string): { startDate: Date; endDate: Date } => {
  const startDate = startOfDay(new Date(), { in: tz(timeZone) });
  const endDate = new Date(startDate);
  endDate.setMilliseconds(endDate.getMilliseconds() - 1);

  return { startDate, endDate };
};

export const getActionLabel = (action: Pick<IAction, "title" | "type">, actionTypes?: ISettingMaster["values"]) => {
  if (action.title) {
    return action.title;
  }
  const actionType = actionTypes?.find((type) => type.value === action.type);
  return actionType?.label || "Type inconnu";
};

export const getActionDateOrPeriod = (
  action: IGetActionsData,
  multipleActions: boolean | undefined,
  timeZone: string
): string => {
  if (isActionApproach(action)) {
    if (!action.executionPeriod?.startDate || !action.executionPeriod?.endDate) {
      return "";
    }
    const start = new Date(action.executionPeriod.startDate);
    const end = new Date(action.executionPeriod.endDate);

    const startDay = startOfDay(new Date(start), { in: tz(timeZone) });
    const endDay = startOfDay(new Date(end), { in: tz(timeZone) });

    if (startDay.getTime() === endDay.getTime()) {
      const hasStartTime = start.getHours() > 0;

      if (multipleActions) {
        return formatDateWithOptionalTime(start, { includeTime: hasStartTime, prefix: "à partir de" }, timeZone);
      }
      return formatDateWithOptionalTime(start, { includeTime: hasStartTime }, timeZone);
    }
    return formatDateRange(start, end, timeZone);
  }
  const date = getActionDate(action);
  if (!date) {
    return "";
  }
  const includeTime = date.getHours() > 0;
  if (multipleActions) {
    return formatDateWithOptionalTime(new Date(date), { includeTime, prefix: "à partir de" }, timeZone);
  }
  return formatDateWithOptionalTime(new Date(date), { includeTime }, timeZone);
};
